import React, { useState } from "react";
import wheel_books_1 from "../assets/wheel_books/wheel_books_1.png";
import wheel_books_2 from "../assets/wheel_books/wheel_books_2.png";
import wheel_books_3 from "../assets/wheel_books/wheel_books_3.png";
import wheel_books_4 from "../assets/wheel_books/wheel_books_4.png";
import wheel_books_5 from "../assets/wheel_books/wheel_books_5.png";
import wheel_books_6 from "../assets/wheel_books/wheel_books_6.png";
import wheel_books_7 from "../assets/wheel_books/wheel_books_7.png";
import wheel_books_8 from "../assets/wheel_books/wheel_books_8.png";
import { useI18n } from "../i18n/I18nProvider";
import { ArrowUpRight } from "lucide-react";

export default function BookWheel() {
  const [rotation, setRotation] = useState(0);
  const { t } = useI18n();
  const [isSpinning, setIsSpinning] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0); // Track which book is at top

  const books = [
    {
      nameKey: "bookWheel.books.book1.name",
      authorKey: "bookWheel.books.book1.author",
      image: wheel_books_1,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/bGTG-PoxR-q2FiALOx2y3g",
    },
    {
      nameKey: "bookWheel.books.book2.name",
      authorKey: "bookWheel.books.book2.author",
      image: wheel_books_2,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/k-mFlJ7eQDqWn-mIEIc6Hw",
    },
    {
      nameKey: "bookWheel.books.book3.name",
      authorKey: "bookWheel.books.book3.author",
      image: wheel_books_3,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/7av1x-MnQJ2BxRQ9elLjvg",
    },
    {
      nameKey: "bookWheel.books.book4.name",
      authorKey: "bookWheel.books.book4.author",
      image: wheel_books_4,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/MfqBJfVIRGO2SfoVQfHxEg",
    },
    {
      nameKey: "bookWheel.books.book5.name",
      authorKey: "bookWheel.books.book5.author",
      image: wheel_books_5,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/oPHsdJdcT-a.PcXQD90Khw",
    },
    {
      nameKey: "bookWheel.books.book6.name",
      authorKey: "bookWheel.books.book6.author",
      image: wheel_books_6,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/JBrS9SD5Tletxj7xEvxspQ",
    },
    {
      nameKey: "bookWheel.books.book7.name",
      authorKey: "bookWheel.books.book7.author",
      image: wheel_books_7,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/Ms8RPR6TSbaFfvXd7lEvsw",
    },
    {
      nameKey: "bookWheel.books.book8.name",
      authorKey: "bookWheel.books.book8.author",
      image: wheel_books_8,
      url: "https://ekatra-summarizer.glide.page/dl/ceb417/s/fc172a/r/9vdaHCJ5SOy0SLYK1Vkjnw",
    },
  ];

  const handleSpin = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    // Rotate by 45 degrees (360/8 books) to move to next book
    setRotation((prev) => prev - 45);

    // Update active index to next book (wraps around after last book)
    setActiveIndex((prev) => (prev + 1) % books.length);

    // Reset spinning state after animation completes
    setTimeout(() => {
      setIsSpinning(false);
    }, 800);
  };

  // Calculate responsive radius based on screen size
  const getRadius = () => {
    if (typeof window !== "undefined") {
      const width = window.innerWidth;
      if (width < 640) return 150; // mobile - increased to prevent overlap
      if (width < 768) return 200; // sm - increased to prevent overlap
      if (width < 1024) return 280; // md - increased to prevent overlap
      return 340; // lg+ - increased from 320
    }
    return 340;
  };

  const [radius, setRadius] = useState(getRadius());

  // Update radius on window resize
  React.useEffect(() => {
    const handleResize = () => setRadius(getRadius());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // NEW: Bottom book instead of top
  const bottomIndex = (activeIndex + 4) % books.length;

  return (
    <section className="py-12 sm:py-16 lg:py-12 bg-[#FDF7EF] text-center overflow-visible">
      <div className="container mx-auto px-2 sm:px-4">
        {/* Responsive container - increased sizes to utilize more space */}
        <div className="relative w-full max-w-[400px] h-[400px] sm:max-w-[520px] sm:h-[520px] md:max-w-[720px] md:h-[720px] lg:max-w-[920px] lg:h-[920px] mx-auto mb-32 sm:mb-40 md:mb-48">
          {/* DECORATIVE SPOKES/ARROWS - Rotate with books */}
          <div
            className="absolute inset-0 transition-transform duration-[800ms] ease-in-out"
            style={{
              transform: `rotate(${rotation}deg)`,
            }}
          >
            {books.map((_, i) => {
              const angle = (i * 360) / books.length;

              const spokeLength = radius * 0.45; // Spokes go halfway to books

              // For the diamond: Check which book is currently at the top after rotation
              const stepAngle = 360 / books.length;

              // How many steps we've rotated, normalized to [0, books.length - 1]
              const rotationSteps =
                Math.round(rotation / stepAngle) % books.length;

              const topPositionIndex = 2; // Top position is at index 2

              const activeBookIndex =
                (topPositionIndex - rotationSteps + books.length) %
                books.length;

              const isSpokeActive = i === activeBookIndex;
              const isDiamondActive = i === activeBookIndex;
              return (
                <React.Fragment key={`spoke-${i}`}>
                  {/* Line from center to arrow */}
                  <div
                    className="absolute top-1/2 left-1/2 transition-opacity duration-800"
                    style={{
                      width: `${spokeLength}px`,
                      height: "4px",
                      backgroundColor: isSpokeActive
                        ? "rgba(232, 167, 43, 1)"
                        : "rgba(232, 167, 43, 0.25)",
                      transform: `translate(0, -50%) rotate(${angle}deg)`,
                      transformOrigin: "0% 50%",
                    }}
                  />

                  {/* Diamond/Arrow shape at the end */}
                  <div
                    className="absolute top-1/2 left-1/2 transition-opacity duration-800"
                    style={{
                      transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(${spokeLength}px)`,
                      opacity: isDiamondActive ? 1 : 0.5,
                    }}
                  >
                    {/* Diamond/Arrow shape */}
                    <div
                      className="w-[16px] h-[16px] sm:w-[24px] sm:h-[24px] md:w-[32px] md:h-[32px] lg:w-[40px] lg:h-[40px] bg-[#E8A72B] rotate-45 rounded-sm"
                      style={{
                        boxShadow: isDiamondActive
                          ? "0 4px 12px rgba(232, 167, 43, 0.4)"
                          : "none",
                      }}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Rotating wheel container */}
          <div
            className="absolute inset-0 transition-transform duration-[800ms] ease-in-out"
            style={{
              transform: `rotate(${rotation}deg)`,
            }}
          >
            {/* BOOKS positioned in circle */}
            {books.map((book, i) => {
              const angle = (i * 360) / books.length;
              const angleRad = (angle - 90) * (Math.PI / 180);
              const x = Math.cos(angleRad) * radius;
              const y = Math.sin(angleRad) * radius;

              return (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 book-position"
                  style={{
                    transform: `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${-rotation}deg)`,
                    transition: "transform 800ms ease-in-out",
                  }}
                >
                  <img
                    src={book.image}
                    alt={`${t(book.nameKey)} by ${t(book.authorKey)}`}
                    onClick={() => {
                        if (i === bottomIndex) {
                          window.open(book.url, "_blank");
                        }
                      }}
                    className="
                        rounded-lg sm:rounded-xl
                        object-contain
                        w-[70px] h-[105px]
                        sm:w-[95px] sm:h-[145px]
                        md:w-[120px] md:h-[185px]
                        lg:w-[165px] lg:h-[235px]
                        hover:scale-110 transition-transform duration-300
                    "
                    style={{ height: "auto" }}
                  />
                </div>
              );
            })}
          </div>

          {/* CENTER CIRCLE – CLICKABLE & RESPONSIVE */}
          <button
            onClick={handleSpin}
            disabled={isSpinning}
            className="
              absolute top-1/2 left-1/2
              -translate-x-1/2 -translate-y-1/2
              w-[110px] h-[110px]
              sm:w-[150px] sm:h-[150px]
              md:w-[200px] md:h-[200px]
              lg:w-[260px] lg:h-[260px]
              xl:w-[200px] xl:h-[200px]
              bg-gradient-to-br from-[#E8A72B] to-[#D4941F]
              border-[5px] sm:border-[6px] md:border-[7px] lg:border-[9px]
              border-[#033049]
              rounded-full 
              flex flex-col items-center justify-center
              text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl 
              font-bold 
              shadow-[0_8px_24px_rgba(3,48,73,0.3)]
              z-20
              hover:scale-105 active:scale-95
              transition-all duration-300
              cursor-pointer
              disabled:cursor-not-allowed
              disabled:opacity-70
              leading-tight
            "
          >
            <span className="text-[#033049]">
              {t("bookWheel.button.line1")}
            </span>
            <span className="text-[#033049]">
              {t("bookWheel.button.line2")}
            </span>
          </button>

          {/*  BOTTOM ACTIVE BOOK */}
          <div className="absolute -bottom-36 sm:-bottom-40 md:-bottom-40 lg:-bottom-44 left-1/2 -translate-x-1/2 text-center w-full max-w-2xl px-4">
            <div className="text-[#033049] font-semibold text-lg sm:text-xl md:text-2xl lg:text-3xl mb-5 sm:mb-6 md:mb-7">
              <span className="font-bold">{t(books[bottomIndex].nameKey)}</span>
              <span className="text-base sm:text-lg md:text-xl lg:text-2xl font-normal opacity-80 ml-2 sm:ml-3">
                {t("bookWheel.by")} {t(books[bottomIndex].authorKey)}
              </span>
            </div>
            <a
              href={books[bottomIndex].url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 sm:px-8 md:px-10 py-3 sm:py-4 bg-ekatraLightBlue text-white rounded-full text-base sm:text-lg md:text-xl font-semibold shadow-md hover:bg-[#072B49] transition"
            >
              {t("bookWheel.cta")}
              <ArrowUpRight
                className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7"
                strokeWidth={2}
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
